import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaLinkedinIn, FaYoutube, FaInstagram } from 'react-icons/fa';
import footerLogo from '../../assets/images/logo-footer.svg';
import theme from '../../theme';

const FooterContainer = styled.footer`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 3rem 0 1rem;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 2rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FooterLogo = styled.div`
  margin-bottom: 1rem;
  
  img {
    height: 40px;
  }
`;

const FooterColumn = styled.div`
  margin-bottom: 1rem;
`;

const FooterTitle = styled.h3`
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.medium};
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: ${theme.colors.primary};
  }
`;

const FooterLink = styled(Link)`
  display: block;
  color: ${theme.colors.lightGray};
  margin-bottom: 0.75rem;
  transition: ${theme.transition};
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${theme.colors.darkGray};
  color: ${theme.colors.white};
  transition: ${theme.transition};
  
  &:hover {
    background-color: ${theme.colors.primary};
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${theme.colors.darkGray};
  color: ${theme.colors.lightGray};
  font-size: ${theme.fontSizes.small};
`;

const PoweredByLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.75rem;
  
  img {
    height: 24px;
    opacity: 0.7;
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 1;
    }
  }
`;

const Footer = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  const footerLinks = [
    {
      titleKey: 'footer.corporate.title',
      links: [
        { titleKey: 'footer.corporate.about', path: `/${lang}/about` },
        { titleKey: 'footer.corporate.references', path: `/${lang}/references` },
        { titleKey: 'footer.corporate.news', path: `/${lang}/news` },
        { titleKey: 'footer.corporate.privacy', path: `/${lang}/privacy-policy` },
        { titleKey: 'footer.corporate.cookies', path: `/${lang}/cookies` },
        { titleKey: 'footer.corporate.gdpr', path: `/${lang}/gdpr` }
      ]
    },
    {
      titleKey: 'footer.solutions.title',
      links: [
        { titleKey: 'footer.solutions.ecommerce', path: `/${lang}/ecommerce` },
        { titleKey: 'footer.solutions.payment', path: `/${lang}/payment` },
        { titleKey: 'footer.solutions.dealer', path: `/${lang}/dealer-management` },
        { titleKey: 'footer.solutions.b2b', path: `/${lang}/b2b-b4b` },
        { titleKey: 'footer.solutions.features', path: `/${lang}/features` },
        { titleKey: 'footer.solutions.benefits', path: `/${lang}/benefits` },
        { titleKey: 'footer.solutions.industries', path: `/${lang}/industries` }
      ]
    },
    {
      titleKey: 'footer.pricing.title',
      links: [
        { titleKey: 'footer.pricing.integration', path: `/${lang}/integration` },
        { titleKey: 'footer.pricing.packages', path: `/${lang}/pricing` }
      ]
    },
    {
      titleKey: 'footer.contact.title',
      links: [
        { titleKey: 'footer.contact.contact', path: `/${lang}/contact` },
        { titleKey: 'footer.contact.demo', path: `/${lang}/demo` },
        { titleKey: 'footer.contact.faq', path: `/${lang}/faq` }
      ]
    }
  ];
  
  return (
    <FooterContainer>
      <div className="container">
        <FooterGrid>
          <FooterColumn>
            <FooterLogo>
              <Link to={`/${lang || 'en'}`}>
                <img src={footerLogo} alt="PetekB2B Logo" loading="lazy" />
              </Link>
            </FooterLogo>
            <p>{t('footer.description')}</p>
            <SocialLinks>
              <SocialLink href="https://www.linkedin.com/products/boranka-petekb2b/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FaLinkedinIn />
              </SocialLink>
              <SocialLink href="https://www.youtube.com/@petekb2b" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <FaYoutube />
              </SocialLink>
              <SocialLink href="https://www.instagram.com/borankatek/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FaInstagram />
              </SocialLink>
            </SocialLinks>
          </FooterColumn>
          
          {footerLinks.map((column, index) => (
            <FooterColumn key={index}>
              <FooterTitle>{t(column.titleKey)}</FooterTitle>
              {column.links.map((link, linkIndex) => (
                <FooterLink key={linkIndex} to={link.path}>
                  {t(link.titleKey)}
                </FooterLink>
              ))}
            </FooterColumn>
          ))}
        </FooterGrid>
        
        <Copyright>
          <p>© {new Date().getFullYear()} PetekB2B. {t('footer.copyright')}</p>
          <PoweredByLink href="https://www.boranka.com" target="_blank" rel="noopener noreferrer">
            <img src={`${process.env.PUBLIC_URL}/design-by-boranka.png`} alt="Design by Boranka" loading="lazy" />
          </PoweredByLink>
        </Copyright>
      </div>
    </FooterContainer>
  );
};

export default Footer; 